import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import * as enrollmentActions from '../../../actions/enrollmentActions'
import { reset as resetDualPanelData } from '../../../reducers/dualPanelSelectorReducer'

import { Tab, Tabs, TabList, TabPanel } from '../../common/Tabs'

import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import Loader from '../../common/loader/Loader'
import ApproveEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/ApproveEnrollmentModal'
import DropEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/DropEnrollmentModal'
import RefundEnrollmentModal from '../../classes/program-dashboard-manager/enrollments-tab/modals/RefundEnrollmentModal'
import WaitlistProgramSelectorModal from '../../classes/program-dashboard-manager/enrollments-tab/waitlist/modals/WaitlistProgramSelectorModal'
import SuccessModal from '../../common/customModal/SuccessModal'
import TransferConfirmationModal from './TransferConfirmationModal'
import ConfirmActionModal from './ConfirmActionModal'
import BillingStatusBadge from './BillingStatusBadge'

import { ReactComponent as CloseIcon } from '../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as RefundIcon } from '../../../assets/images/common/icons/history-icon.svg'
import { ReactComponent as CheckmarkIcon } from '../../../assets/images/common/icons/checkbox-checkmark.svg'
import { ReactComponent as EditIcon } from '../../../assets/images/common/icons/edit-icon.svg'
import { ReactComponent as TransferIcon } from '../../../assets/images/common/icons/transfer-icon.svg'
import { ReactComponent as PlusIcon } from '../../../assets/images/common/icons/circle-plus-icon.svg'
import { ReactComponent as DeleteIcon } from '../../../assets/images/common/icons/delete-icon.svg'
import { ReactComponent as WarningIcon } from '../../../assets/images/common/icons/warning-icon.svg'
import { ReactComponent as PersonIcon } from '../../../assets/images/common/icons/person-icon.svg'
import { ReactComponent as FamilyIcon } from '../../../assets/images/common/icons/family-icon.svg'
import { ReactComponent as CallIcon } from '../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as CopyIcon } from '../../../assets/images/common/icons/copy-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/images/common/icons/calendar-icon.svg'
import { ReactComponent as GolferIcon } from '../../../assets/images/common/icons/golfer-half-icon.svg'
import { ReactComponent as BillingXCircle } from '../../../assets/images/common/icons/billing-x-circle.svg'
import { ReactComponent as ReceiptIcon } from '../../../assets/images/common/icons/money-check-dollar-circle.svg'

import { requestGetEnrollment } from '../../../actions/enrollmentsBillingActions'
import RefundInvoiceModal from './RefundInvoiceModal'
import CreateEnrollmentModal from '../../classes/program-dashboard-manager/add-students-modal/CreateEnrollmentModal'
import useInvoiceActions from './useInvoiceActions'
import useDropActions from './useDropActions'
import { deserialize } from '../../../utility/serialization'
import { genderLabel } from '../../../utility/userHelper'
import { formatPhoneForLinks } from '../../../utility/phoneHelper'
import {
  enrollmentStatusLabel,
  activityLabel,
  formatInvoiceStatusDisplay,
  formatPaymentMethodDisplay,
  wasPaidOnline,
} from '../../../utility/enrollmentsBilling'
import { displayWithCurrency } from '../../../utility/currency'

import classes from './EnrollmentDetailDrawer.module.scss'
import colors from '../../../assets/styles/globals.scss'


// Drop & Refund applies whenever there's real money to give back: an online payment (Stripe holds it
// and refunds in full server-side, with or without an invoice), or a paid invoice. An offline "paid"
// enrollment with no invoice has nothing to refund, so it falls back to the legacy Drop modal.
const needsDropRefund = (enrollment) => {
  if (enrollment?.paymentStatus === 'paid_online') { return true }

  const invoice = enrollment?.invoice
  return !!invoice && (invoice.status === 'paid' || enrollment?.paymentStatus === 'paid')
}

// Which drop modal to open: the R3 Drop/Refund modal refunds against an invoice, so an enrollment
// with money to refund but no invoice uses the pre-invoicing RefundEnrollmentModal instead.
const dropModalFor = (enrollment) => {
  if (!needsDropRefund(enrollment)) { return 'drop' }

  return enrollment?.invoice ? 'dropRefund' : 'dropRefundLegacy'
}

// RefundEnrollmentModal predates E&B and reads the roster's enrollment shape (`user`, `paid_at`),
// while the drawer loads the E&B shape (`student`, `paidAt`). Adapt here rather than touching the
// shared modal, so the roster flow is unaffected.
const legacyRefundEnrollment = enrollment => ({
  ...enrollment,
  user: enrollment.student,
  paid_at: enrollment.paidAt,
  // The serializer omits fees until the Stripe payment entry lands; the modal indexes into it.
  fees: enrollment.fees || {},
})

const EnrollmentDetailDrawer = ({ enrollment: listEnrollment, communityId, onClose, onActionComplete }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [enrollment, setEnrollment] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const [activeModal, setActiveModal] = useState(null) // 'approve' | 'drop' | 'remove' | null
  const [actionLoading, setActionLoading] = useState(false)
  const [coachMessage, setCoachMessage] = useState('')
  const [notifyViaEmail, setNotifyViaEmail] = useState(true)

  // Transfer flow (reuses the waitlist program selector)
  const [showTransfer, setShowTransfer] = useState(false)
  const [showTransferConfirm, setShowTransferConfirm] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [showTransferSuccess, setShowTransferSuccess] = useState(false)

  const loadEnrollment = useCallback(() => {
    setIsLoading(true)
    return requestGetEnrollment({ communityId, enrollmentId: listEnrollment.id })
      .then(response => {
        const data = deserialize(response.data)
        setEnrollment(data.enrollment)
      })
      .catch(() => {
        toast.error(t('enrollmentsBilling.drawer.failedToLoadEnrollment'), { position: toast.POSITION.TOP_RIGHT })
      })
      .finally(() => setIsLoading(false))
  }, [communityId, listEnrollment.id])

  useEffect(() => {
    loadEnrollment()
  }, [loadEnrollment])

  const closeModal = () => {
    setActiveModal(null)
    setCoachMessage('')
    setNotifyViaEmail(true)
  }

  const handleApprove = () => {
    setActionLoading(true)
    dispatch(enrollmentActions.updateEnrollment({ enrollmentData: { id: listEnrollment.id, status: 'active' }, coachMessage })).unwrap()
      .then(() => {
        toast.success(t('enrollmentsBilling.drawer.enrollmentApproved'), { position: toast.POSITION.TOP_RIGHT })
        closeModal()
        onActionComplete?.()
        loadEnrollment()
      })
      .catch(() => toast.error(t('enrollmentsBilling.drawer.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  // "Delete" (and the pending "Decline") soft-deletes the enrollment via the deleteEnrollment thunk:
  // DELETE /api/v1/enrollments/:id. The record is discarded and leaves the list. For online-paid
  // enrollments the refund flag is passed through (RefundEnrollmentModal), mirroring the roster drop.
  const handleRemoveEnrollment = (refund = false) => {
    const wasActive = (enrollment || listEnrollment)?.status === 'active'
    setActionLoading(true)
    dispatch(enrollmentActions.deleteEnrollment({ enrollmentId: listEnrollment.id, refund, coachMessage, notifyViaEmail })).unwrap()
      .then(() => {
        const key = wasActive ? 'enrollmentDeleted' : 'enrollmentDeclined'
        toast.success(t(`enrollmentsBilling.drawer.${key}`), { position: toast.POSITION.TOP_RIGHT })
        closeModal()
        onActionComplete?.()
        onClose()
      })
      .catch(() => toast.error(t('enrollmentsBilling.drawer.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  const {
    invoiceHandlers, handleMarkPaid, handleRefund, handleEditInvoice, handleCreateInvoice, handleMarkOutstanding,
    showMarkPaidSuccess, setShowMarkPaidSuccess, markPaidEmailSent, showReminderSuccess, setShowReminderSuccess,
  } = useInvoiceActions({ enrollment, setActiveModal, setActionLoading, onActionComplete, loadEnrollment })

  const { handleDrop, handleDropNoRefund, handleDropRefund, dropSuccessTitle, closeDropSuccess } = useDropActions({
    listEnrollment, enrollment, coachMessage, notifyViaEmail, setActionLoading, closeModal, onActionComplete, loadEnrollment,
    refundOnly: handleRefund,
  })

  // Flip the enrollment's payment status directly (offline paid <-> outstanding). No invoice is
  // touched — this is only for enrollments tracked by payment_status alone.
  const handleMarkPaymentStatus = (paymentStatus) => {
    setActionLoading(true)
    dispatch(enrollmentActions.updateEnrollment({ enrollmentData: { id: listEnrollment.id, paymentStatus } })).unwrap()
      .then(() => {
        const key = paymentStatus === 'paid' ? 'enrollmentsBilling.drawer.markedPaidStatus' : 'enrollmentsBilling.drawer.markedOutstandingStatus'
        toast.success(t(key), { position: toast.POSITION.TOP_RIGHT })
        onActionComplete?.()
        loadEnrollment()
      })
      .catch(() => toast.error(t('enrollmentsBilling.drawer.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  const closeTransfer = () => {
    setShowTransfer(false)
    setShowTransferConfirm(false)
    setSelectedCourse(null)
    dispatch(resetDualPanelData())
  }

  const handleTransfer = ({ coachMessage: message, sendEmail }) => {
    if (!selectedCourse) { return }

    setActionLoading(true)
    dispatch(enrollmentActions.transferEnrollment({
      enrollmentId: listEnrollment.id, courseId: selectedCourse.id, coachMessage: message, sendEmail,
    })).unwrap()
      .then(() => {
        closeTransfer()
        onActionComplete?.()
        loadEnrollment()
        setShowTransferSuccess(true)
      })
      .catch(() => toast.error(t('enrollmentsBilling.drawer.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }


  const student = enrollment?.student || listEnrollment.student
  const displayStatus = enrollment?.displayStatus || listEnrollment.displayStatus
  // The Billing tab is always available once the enrollment loads; when there's no invoice it shows
  // the "No Billing Available" empty state (attach a package to activate billing).
  const showBillingTab = !!enrollment
  const invoice = enrollment?.invoice
  const isOutstanding = invoice?.status === 'outstanding'
  const dropModal = dropModalFor(enrollment)

  return (
    <>
      <div className={classes.drawer}>
        <header className={classes.header}>
          <button className={classes.closeButton} onClick={onClose}>
            <CloseIcon className={classes.closeIcon} aria-hidden='true' />
          </button>
        </header>

        {isOutstanding && invoice?.lastReminderSentAt && (
          <OutstandingBanner lastReminderDate={invoice.lastReminderSentAt} />
        )}

        <Tabs className={classes.tabs}>
          <TabList className={classes.tabList}>
            <Tab className={classes.tab}>{t('enrollmentsBilling.drawer.tabEnrollments')}</Tab>
            {showBillingTab && <Tab className={classes.tab}>{t('enrollmentsBilling.drawer.tabBilling')}</Tab>}
            <Tab className={classes.tab}>{t('enrollmentsBilling.drawer.tabContactInfo')}</Tab>
          </TabList>

          <StudentHeader
            student={student}
            displayStatus={displayStatus}
            actionLoading={actionLoading}
            onApprove={() => setActiveModal('approve')}
            onDecline={() => setActiveModal('remove')}
          />

          <TabPanel>
            {isLoading || !enrollment ? (
              <div className={classes.loaderWrapper}>
                {isLoading ? <Loader>{t('enrollmentsBilling.drawer.loadingDetails')}</Loader> :
                  <span>{t('enrollmentsBilling.drawer.failedToLoadDetails')}</span>}
              </div>
            ) : (
              <EnrollmentTab
                enrollment={enrollment}
                displayStatus={displayStatus}
                onDrop={() => setActiveModal(dropModal)}
                onRemove={() => setActiveModal('delete')}
                onTransfer={() => setShowTransfer(true)}
              />
            )}
          </TabPanel>
          {showBillingTab && (
            <TabPanel>
              <BillingTab
                enrollment={enrollment}
                invoiceHandlers={invoiceHandlers}
                onMarkPaymentStatus={handleMarkPaymentStatus}
                actionLoading={actionLoading}
              />
            </TabPanel>
          )}
          <TabPanel>
            {enrollment ? <ContactTab enrollment={enrollment} /> : null}
          </TabPanel>
        </Tabs>
      </div>
      <div
        tabIndex={0}
        role='button'
        onKeyDown={() => null}
        className={classes.drawerOverlay}
        onClick={onClose}
      />

      <EnrollmentActionModals
        activeModal={activeModal}
        closeModal={closeModal}
        coachMessage={coachMessage}
        setCoachMessage={setCoachMessage}
        notifyViaEmail={notifyViaEmail}
        toggleNotify={() => setNotifyViaEmail(notify => !notify)}
        isLoading={actionLoading}
        onApprove={handleApprove}
        onDrop={handleDrop}
        onDropNoRefund={handleDropNoRefund}
        onDropRefund={handleDropRefund}
        onRemove={() => handleRemoveEnrollment(false)}
        dropEnrollment={enrollment || listEnrollment}
        invoice={invoice}
        dropSuccessTitle={dropSuccessTitle}
        closeDropSuccess={closeDropSuccess}
      />

      <TransferModals
        enrollment={enrollment}
        showTransfer={showTransfer}
        showTransferConfirm={showTransferConfirm}
        showTransferSuccess={showTransferSuccess}
        selectedCourse={selectedCourse}
        onSelectProgram={setSelectedCourse}
        closeTransfer={closeTransfer}
        onProceedToConfirm={() => { setShowTransfer(false); setShowTransferConfirm(true) }}
        onConfirmTransfer={handleTransfer}
        closeTransferSuccess={() => setShowTransferSuccess(false)}
        isLoading={actionLoading}
      />

      <InvoiceModals
        activeModal={activeModal}
        closeModal={closeModal}
        enrollment={enrollment}
        invoice={invoice}
        actionLoading={actionLoading}
        handleMarkPaid={handleMarkPaid}
        handleRefund={handleRefund}
        handleEditInvoice={handleEditInvoice}
        handleCreateInvoice={handleCreateInvoice}
        handleMarkOutstanding={handleMarkOutstanding}
        showMarkPaidSuccess={showMarkPaidSuccess}
        setShowMarkPaidSuccess={setShowMarkPaidSuccess}
        markPaidEmailSent={markPaidEmailSent}
        showReminderSuccess={showReminderSuccess}
        setShowReminderSuccess={setShowReminderSuccess}
      />
    </>
  )
}

// Student avatar + name/email, with inline approve/decline actions for pending enrollments.
const StudentHeader = ({ student, displayStatus, actionLoading, onApprove, onDecline }) => {
  const { t } = useTranslation()
  return (
    <div className={classes.studentHeader}>
      <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
      <div className={classes.studentInfo}>
        <h6 className='mb-0 font-weight-bold'>{student.firstName} {student.lastName}</h6>
        <span className={classes.studentMeta}>{student.email}</span>
      </div>
      {displayStatus === 'pending_approval' && (
        <div className={classes.inlineActions}>
          <button className={classes.approveButton} onClick={onApprove} disabled={actionLoading}>
            {t('enrollmentsBilling.drawer.approve')}
          </button>
          <button className={classes.declineButton} onClick={onDecline} disabled={actionLoading}>
            {t('enrollmentsBilling.drawer.decline')}
          </button>
        </div>
      )}
    </div>
  )
}

StudentHeader.propTypes = {
  student: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  actionLoading: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
}

// Transfer flow modals: program selector → confirm → success.
const TransferModals = ({
  enrollment, showTransfer, showTransferConfirm, showTransferSuccess, selectedCourse,
  onSelectProgram, closeTransfer, onProceedToConfirm, onConfirmTransfer, closeTransferSuccess, isLoading,
}) => {
  const { t } = useTranslation()
  return (
    <>
      {showTransfer && enrollment?.course && (
        <WaitlistProgramSelectorModal
          isOpen={showTransfer}
          title={t('enrollmentsBilling.drawer.transferToAnotherProgram')}
          currentProgram={enrollment.course}
          selectedProgram={selectedCourse}
          onSelectProgram={onSelectProgram}
          students={enrollment.student ? [enrollment.student] : []}
          onClose={closeTransfer}
          onProceed={onProceedToConfirm}
        />
      )}

      {showTransferConfirm && enrollment && (
        <TransferConfirmationModal
          isOpen
          enrollment={enrollment}
          onClose={closeTransfer}
          onConfirm={onConfirmTransfer}
          isLoading={isLoading}
        />
      )}

      <SuccessModal
        isOpen={showTransferSuccess}
        onClose={closeTransferSuccess}
        title={t('enrollmentsBilling.drawer.enrollmentTransferred')}
        buttonMessage={t('enrollmentsBilling.drawer.close')}
      />
    </>
  )
}

TransferModals.propTypes = {
  enrollment: PropTypes.object,
  showTransfer: PropTypes.bool,
  showTransferConfirm: PropTypes.bool,
  showTransferSuccess: PropTypes.bool,
  selectedCourse: PropTypes.object,
  onSelectProgram: PropTypes.func.isRequired,
  closeTransfer: PropTypes.func.isRequired,
  onProceedToConfirm: PropTypes.func.isRequired,
  onConfirmTransfer: PropTypes.func.isRequired,
  closeTransferSuccess: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
}

// Invoice lifecycle modals (R3): mark-as-paid success + the paid/refund/edit modals.
const InvoiceModals = ({
  activeModal, closeModal, enrollment, invoice, actionLoading,
  handleMarkPaid, handleRefund, handleEditInvoice, handleCreateInvoice, handleMarkOutstanding,
  showMarkPaidSuccess, setShowMarkPaidSuccess, markPaidEmailSent, showReminderSuccess, setShowReminderSuccess,
}) => {
  const { t } = useTranslation()
  return (
    <>
      <SuccessModal
        isOpen={showMarkPaidSuccess}
        onClose={() => setShowMarkPaidSuccess(false)}
        title={markPaidEmailSent ? t('enrollmentsBilling.drawer.markedAsPaidWithEmail') : t('enrollmentsBilling.drawer.markedAsPaid')}
        buttonMessage={t('enrollmentsBilling.drawer.close')}
      />
      <SuccessModal
        isOpen={showReminderSuccess}
        onClose={() => setShowReminderSuccess(false)}
        title={t('enrollmentsBilling.drawer.reminderEmailSent')}
        buttonMessage={t('enrollmentsBilling.drawer.close')}
      />
      {activeModal === 'invoicePaid' && enrollment && (
        <CreateEnrollmentModal
          isOpen
          mode='markAsPaid'
          onClose={closeModal}
          enrollment={enrollment}
          onSubmit={handleMarkPaid}
          isLoading={actionLoading}
        />
      )}
      {activeModal === 'invoiceRefund' && invoice && (
        <RefundInvoiceModal
          isOpen
          onClose={closeModal}
          onSubmit={handleRefund}
          isLoading={actionLoading}
          invoice={invoice}
          enrollment={enrollment}
        />
      )}
      {activeModal === 'invoiceOutstanding' && invoice && (
        <ConfirmActionModal
          isOpen
          onClose={closeModal}
          onConfirm={handleMarkOutstanding}
          isLoading={actionLoading}
          title={t('enrollmentsBilling.drawer.markAsOutstandingTitle')}
          message={t('enrollmentsBilling.drawer.markAsOutstandingMessage')}
          confirmMessage={t('enrollmentsBilling.drawer.markAsUnpaid')}
        />
      )}
      {activeModal === 'invoiceEdit' && enrollment?.invoice && (
        <CreateEnrollmentModal
          isOpen
          mode='edit'
          onClose={closeModal}
          enrollment={enrollment}
          program={enrollment.course}
          onSubmit={handleEditInvoice}
          isLoading={actionLoading}
        />
      )}
      {/* No invoice yet: the same edit modal attaches a package, which creates the invoice. */}
      {activeModal === 'invoiceEdit' && enrollment && !enrollment.invoice && (
        <CreateEnrollmentModal
          isOpen
          mode='edit'
          onClose={closeModal}
          enrollment={enrollment}
          program={enrollment.course}
          onSubmit={handleCreateInvoice}
          isLoading={actionLoading}
        />
      )}
    </>
  )
}

InvoiceModals.propTypes = {
  activeModal: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  enrollment: PropTypes.object,
  invoice: PropTypes.object,
  actionLoading: PropTypes.bool,
  handleMarkPaid: PropTypes.func.isRequired,
  handleRefund: PropTypes.func.isRequired,
  handleEditInvoice: PropTypes.func.isRequired,
  handleCreateInvoice: PropTypes.func.isRequired,
  handleMarkOutstanding: PropTypes.func.isRequired,
  showMarkPaidSuccess: PropTypes.bool,
  setShowMarkPaidSuccess: PropTypes.func.isRequired,
  markPaidEmailSent: PropTypes.bool,
  showReminderSuccess: PropTypes.bool,
  setShowReminderSuccess: PropTypes.func.isRequired,
}

// --- Action Modals (approve / drop / delete / refund) ---

const EnrollmentActionModals = ({
  activeModal, closeModal, coachMessage, setCoachMessage, notifyViaEmail, toggleNotify,
  isLoading, onApprove, onDrop, onDropNoRefund, onDropRefund, onRemove, dropEnrollment, invoice, dropSuccessTitle, closeDropSuccess,
}) => {
  const { t } = useTranslation()
  return (
    <>
      {activeModal === 'approve' && (
        <ApproveEnrollmentModal
          showModal
          closeModal={closeModal}
          coachMessage={coachMessage}
          handleCoachMessageChange={e => setCoachMessage(e.target.value)}
          handleApproveEnrollment={onApprove}
          isLoading={isLoading}
        />
      )}

      {(activeModal === 'drop' || activeModal === 'remove') && (
        <DropEnrollmentModal
          showModal
          closeModal={closeModal}
          coachMessage={coachMessage}
          handleCoachMessageChange={e => setCoachMessage(e.target.value)}
          handleRemoveEnrollment={activeModal === 'drop' ? onDrop : onRemove}
          enrollment={dropEnrollment}
          notifyViaEmail={notifyViaEmail}
          handleCheckboxChange={toggleNotify}
          isLoading={isLoading}
        />
      )}

      {/* Dropping a paid enrollment that has an R3 invoice: refund (full or partial) against it. */}
      {activeModal === 'dropRefund' && dropEnrollment && (
        <RefundInvoiceModal
          isOpen
          mode='dropRefund'
          onClose={closeModal}
          onSubmit={onDropRefund}
          isLoading={isLoading}
          enrollment={dropEnrollment}
          invoice={invoice}
        />
      )}

      {/* Paid online with no invoice: the pre-invoicing modal — drop with a full refund, or without one. */}
      {activeModal === 'dropRefundLegacy' && dropEnrollment && (
        <RefundEnrollmentModal
          showModal
          closeModal={closeModal}
          coachMessage={coachMessage}
          handleCoachMessageChange={e => setCoachMessage(e.target.value)}
          handleRemoveEnrollment={refund => (refund ? onDrop() : onDropNoRefund())}
          enrollment={legacyRefundEnrollment(dropEnrollment)}
          notifyViaEmail={notifyViaEmail}
          handleCheckboxChange={toggleNotify}
          isLoading={isLoading}
        />
      )}

      {activeModal === 'delete' && (
        <ConfirmActionModal
          isOpen
          onClose={closeModal}
          onConfirm={onRemove}
          isLoading={isLoading}
          title={t('enrollmentsBilling.drawer.deleteEnrollment')}
          message={t('enrollmentsBilling.drawer.deleteEnrollmentMessage')}
          confirmMessage={t('enrollmentsBilling.drawer.deleteEnrollment')}
          cancelMessage={t('enrollmentsBilling.drawer.exit')}
        />
      )}

      <SuccessModal
        isOpen={!!dropSuccessTitle}
        onClose={closeDropSuccess}
        title={dropSuccessTitle}
        buttonMessage={t('enrollmentsBilling.drawer.close')}
      />
    </>
  )
}

EnrollmentActionModals.propTypes = {
  activeModal: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  coachMessage: PropTypes.string,
  setCoachMessage: PropTypes.func.isRequired,
  notifyViaEmail: PropTypes.bool,
  toggleNotify: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onDropNoRefund: PropTypes.func.isRequired,
  onDropRefund: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  dropEnrollment: PropTypes.object,
  invoice: PropTypes.object,
  dropSuccessTitle: PropTypes.string,
  closeDropSuccess: PropTypes.func.isRequired,
}

// --- Outstanding Payment Banner ---

const OutstandingBanner = ({ lastReminderDate }) => {
  const { t } = useTranslation()
  return (
    <div className={classes.outstandingBanner}>
      <WarningIcon className={classes.bannerIcon} />
      <span>{t('enrollmentsBilling.drawer.outstandingPaymentReminder', { date: moment(lastReminderDate).format('M/DD/YYYY') })}</span>
    </div>
  )
}

OutstandingBanner.propTypes = {
  lastReminderDate: PropTypes.string.isRequired,
}

// --- Enrollment Action Buttons ---

const EnrollmentActionButtons = ({ displayStatus, invoice, paidOnline, onDrop, onRemove, onTransfer }) => {
  const { t } = useTranslation()
  const isPending = displayStatus === 'pending_approval'
  const isWaitlisted = displayStatus === 'waitlisted'
  const isDropped = displayStatus === 'dropped'
  const isCompleted = displayStatus === 'completed'

  if (isPending) { return null }

  const isPaid = invoice?.status === 'paid'
  const isRefunded = invoice?.status === 'refunded' || invoice?.status === 'partially_refunded'
  // An online payment is refundable even with no invoice, so it reads "Drop & Refund" too.
  const showRefundLabel = paidOnline || isPaid || isRefunded

  if (isWaitlisted) {
    return (
      <div className={classes.actionButtons}>
        <button className={classes.actionButton} disabled>
          <PlusIcon className={classes.actionIcon} /> {t('enrollmentsBilling.drawer.addToProgram')}
        </button>
        <button className={classes.actionButton} disabled>
          {t('enrollmentsBilling.drawer.sendInviteLink')}
        </button>
        <button className={classNames(classes.actionButton, classes.pushRight)} disabled>
          <DeleteIcon className={classes.actionIcon} /> {t('enrollmentsBilling.drawer.remove')}
        </button>
      </div>
    )
  }

  // Completed or dropped enrollments can only be deleted, and only when not paid online.
  if (isDropped || isCompleted) {
    if (paidOnline) { return null }

    return (
      <div className={classes.actionButtons}>
        <button className={classes.actionButton} onClick={onRemove}>
          <DeleteIcon className={classes.actionIcon} /> {t('enrollmentsBilling.drawer.delete')}
        </button>
      </div>
    )
  }

  return (
    <div className={classes.actionButtons}>
      <button className={classes.actionButton} onClick={onDrop}>
        <RefundIcon className={classes.actionIcon} />{' '}
        {showRefundLabel ? t('enrollmentsBilling.drawer.dropRefund') : t('enrollmentsBilling.drawer.drop')}
      </button>
      <button className={classes.actionButton} onClick={onTransfer}>
        <TransferIcon className={classes.actionIcon} /> {t('enrollmentsBilling.drawer.transfer')}
      </button>
    </div>
  )
}

EnrollmentActionButtons.propTypes = {
  displayStatus: PropTypes.string,
  invoice: PropTypes.object,
  paidOnline: PropTypes.bool,
  onDrop: PropTypes.func,
  onRemove: PropTypes.func,
  onTransfer: PropTypes.func,
}

// --- Enrollment Tab ---

const EnrollmentTab = ({ enrollment, displayStatus, onDrop, onRemove, onTransfer }) => {
  const { t } = useTranslation()
  const { course, package: pkg, invoice } = enrollment

  const isPending = displayStatus === 'pending_approval'
  const isWaitlisted = displayStatus === 'waitlisted'
  const sectionTitle = (isPending || isWaitlisted) ? t('enrollmentsBilling.drawer.details') : t('enrollmentsBilling.drawer.enrollmentDetails')
  const showPackage = !isWaitlisted && !!pkg
  const showStatusBadge = !isPending && !isWaitlisted

  // Merge and sort all activities chronologically (newest first)
  const allActivities = useMemo(() => {
    const merged = [
      ...(enrollment.enrollmentActivities || []).map(a => ({ ...a, source: 'enrollment' })),
      ...(enrollment.invoiceActivities || []).map(a => ({ ...a, source: 'invoice' })),
    ]
    return merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }, [enrollment.enrollmentActivities, enrollment.invoiceActivities])

  const familyAdminEmail = enrollment.student?.familyAdmins?.[0]?.email

  return (
    <div className={classes.tabContent}>
      <EnrollmentActionButtons
        displayStatus={displayStatus}
        invoice={invoice}
        paidOnline={wasPaidOnline(enrollment)}
        onDrop={onDrop} onRemove={onRemove}
        onTransfer={onTransfer}
      />

      <section className={classes.detailCard}>
        <div className={classes.detailCardHeader}>
          <h6 className={classNames(classes.sectionTitle, classes.cardTitle)}>{sectionTitle}</h6>
          {showStatusBadge && (
            <span className={classNames(classes.statusBadge, classes[`status_${displayStatus}`])}>
              {enrollmentStatusLabel(displayStatus, t)}
            </span>
          )}
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.program')}</span>
          <span className={classes.detailValue}>{course?.name}</span>
        </div>

        {showPackage && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.package')}</span>
            <span className={classes.detailValue}>{pkg.name}</span>
          </div>
        )}

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.facility')}</span>
          <span className={classes.detailValue}>{course?.communityName || '-'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.coach')}</span>
          <span className={classes.detailValue}>{course?.coachName || '-'}</span>
        </div>

        {familyAdminEmail && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.familyAdminEmail')}</span>
            <span className={classes.detailValue}>{familyAdminEmail}</span>
          </div>
        )}

        {course?.startDate && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.programDates')}</span>
            <span className={classes.detailValue}>
              {moment(course.startDate).format('MM/DD/YYYY')}
              {course.endDate && `-${moment(course.endDate).format('MM/DD/YYYY')}`}
            </span>
          </div>
        )}

        {allActivities.length > 0 && (
          <div className={classes.updatesBlock}>
            <h6 className={classes.sectionTitle}>{t('enrollmentsBilling.drawer.allUpdates')}</h6>
            <div className={classes.activityTimeline}>
              {allActivities.map(activity => (
                <div key={`${activity.source}-${activity.id}`} className={classes.activityItem}>
                  <div className={classes.activityDot} />
                  <div className={classes.activityContent}>
                    <span className={classes.activityDate}>
                      {moment(activity.createdAt).format('MM/DD/YYYY')}
                    </span>
                    <span className={classes.activityLabel}>
                      {activityLabel(activity.changeType, t)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

EnrollmentTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  onDrop: PropTypes.func,
  onRemove: PropTypes.func,
  onTransfer: PropTypes.func,
}

// --- Billing Tab ---

// editLocked: billing is read-only once the enrollment is dropped, or was paid online (Stripe holds
const deriveBillingFlags = enrollment => ({
  editLocked: enrollment.displayStatus === 'dropped' || enrollment.paymentStatus === 'paid_online',
  isPaidStatus: enrollment.paymentStatus === 'paid',
  canMarkStatus: enrollment.paymentStatus !== 'paid_online'
    && !['pending_approval', 'waitlisted', 'dropped', 'completed'].includes(enrollment.displayStatus),
})

const BillingTab = ({ enrollment, invoiceHandlers, onMarkPaymentStatus, actionLoading }) => {
  const { t } = useTranslation()
  const { invoice, course } = enrollment
  const { editLocked, isPaidStatus, canMarkStatus } = deriveBillingFlags(enrollment)

  // No invoice yet → empty state. Attaching a package (Edit / Add Pricing Package → the edit modal)
  // creates the invoice. The coach message from enrollment creation surfaces as Additional Notes.
  if (!invoice) {
    const createdActivity = (enrollment.enrollmentActivities || []).find(activity => activity.changeType === 'created')
    const coachMessage = createdActivity?.details?.coachMessage

    return (
      <div className={classes.tabContent}>
        {!editLocked && (
          <div className={classes.actionButtons}>
            {canMarkStatus && (
              <button
                className={classes.actionButton}
                onClick={() => onMarkPaymentStatus(isPaidStatus ? 'outstanding' : 'paid')}
                disabled={actionLoading}
              >
                {isPaidStatus ? (
                  t('enrollmentsBilling.drawer.markAsOutstanding')
                ) : (
                  <>
                    <span className={classes.markPaidCheckbox}><CheckmarkIcon className={classes.markPaidCheckmark} /></span>
                    {t('enrollmentsBilling.drawer.markAsPaid')}
                  </>
                )}
              </button>
            )}
            <button className={classNames(classes.actionButton, classes.pushRight)} onClick={invoiceHandlers.onEdit} disabled={actionLoading}>
              <EditIcon className={classes.billingActionIcon} /> {t('enrollmentsBilling.drawer.edit')}
            </button>
          </div>
        )}

        <section className={classes.emptyBillingCard}>
          <span className={classes.noPackageBadge}>
            <BillingXCircle className={classes.noPackageBadgeIcon} /> {t('enrollmentsBilling.drawer.noPricingPackage')}
          </span>

          <div className={classes.emptyBilling}>
            <ReceiptIcon className={classes.emptyBillingIcon} />
            <h6 className={classes.emptyBillingTitle}>{t('enrollmentsBilling.drawer.noBillingAvailable')}</h6>
            <p className={classes.emptyBillingText}>{t('enrollmentsBilling.drawer.noBillingDescription')}</p>
            {!editLocked && (
              <button className={classes.addPackageButton} onClick={invoiceHandlers.onEdit} disabled={actionLoading}>
                {t('enrollmentsBilling.drawer.addPricingPackage')}
              </button>
            )}
          </div>

          {coachMessage && (
            <div className={classes.notesSection}>
              <span className={classes.notesTitle}>{t('enrollmentsBilling.drawer.additionalNotes')}</span>
              <p className={classes.notesText}>{coachMessage}</p>
            </div>
          )}
        </section>
      </div>
    )
  }

  const invoiceDisplay = formatInvoiceStatusDisplay(invoice, t)
  const lineItems = invoice.lineItems || []
  const currency = invoice.currencyCode ? { code: invoice.currencyCode } : null
  const familyAdminEmail = enrollment.student?.familyAdmins?.[0]?.email

  return (
    <div className={classes.tabContent}>
      <BillingActionButtons invoice={invoice} editLocked={editLocked} {...invoiceHandlers} disabled={actionLoading} />

      <section className={classes.invoiceCard}>
        <div className={classes.invoiceHeader}>
          <h6 className={classNames(classes.sectionTitle, classes.cardTitle)}>
            {t('enrollmentsBilling.drawer.invoiceNumber', { number: invoice.invoiceNumber })}
          </h6>
          <BillingStatusBadge display={invoiceDisplay} />
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.program')}</span>
          <span className={classes.detailValue}>{course?.name}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.facility')}</span>
          <span className={classes.detailValue}>{course?.communityName || '-'}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.coach')}</span>
          <span className={classes.detailValue}>{course?.coachName || '-'}</span>
        </div>

        {familyAdminEmail && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.familyAdminEmail')}</span>
            <span className={classes.detailValue}>{familyAdminEmail}</span>
          </div>
        )}

        {course?.startDate && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.programDates')}</span>
            <span className={classes.detailValue}>
              {moment(course.startDate).format('MM/DD/YYYY')}
              {course.endDate && `-${moment(course.endDate).format('MM/DD/YYYY')}`}
            </span>
          </div>
        )}

        {invoice.paymentMethod && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.paymentMethod')}</span>
            <span
              className={classNames(classes.detailValue, classes.paymentMethodValue, {
                [classes.paymentMethodValuePaid]: invoice.status === 'paid',
              })}
            >
              {formatPaymentMethodDisplay(invoice, t)}
            </span>
          </div>
        )}

        {lineItems.length > 0 && (
          <div className={classes.lineItemsSection}>
            {lineItems.map(item => (
              <div key={item.id} className={classes.lineItemRow}>
                <span className={classes.lineItemLabel}>
                  {item.lineType === 'discount' ? t('enrollmentsBilling.drawer.discount') : item.description}
                </span>
                <span className={classes.lineItemAmount}>
                  {currency ? displayWithCurrency(Math.abs(parseFloat(item.amount)), currency) : `$${Math.abs(parseFloat(item.amount)).toFixed(2)}`}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className={classes.totalRow}>
          <span className={classes.totalLabel}>{t('enrollmentsBilling.drawer.total')}</span>
          <span className={classes.totalAmount}>
            {currency ? displayWithCurrency(invoice.total, currency) : `$${parseFloat(invoice.total).toFixed(2)}`}
          </span>
        </div>

        {invoice.notes && (
          <div className={classes.notesSection}>
            <span className={classes.notesTitle}>{t('enrollmentsBilling.drawer.additionalNotes')}</span>
            <p className={classes.notesText}>{invoice.notes}</p>
          </div>
        )}
      </section>
    </div>
  )
}

BillingTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
  invoiceHandlers: PropTypes.object,
  onMarkPaymentStatus: PropTypes.func,
  actionLoading: PropTypes.bool,
}

// --- Billing Action Buttons ---

const BillingActionButtons = ({ invoice, editLocked, onSendReminder, onMarkPaid, onEdit, onRefund, onMarkOutstanding, disabled }) => {
  const { t } = useTranslation()
  if (!invoice) { return null }

  const isOutstanding = invoice.status === 'outstanding'
  const isFullyRefunded = invoice.status === 'refunded'
  const isPaid = invoice.status === 'paid'

  if (isOutstanding) {
    return (
      <div className={classes.actionButtons}>
        <button className={classes.actionButton} onClick={onSendReminder} disabled={disabled}>
          <MailIcon className={classes.billingActionIcon} /> {t('enrollmentsBilling.drawer.sendReminder')}
        </button>
        <button className={classNames(classes.actionButton, classes.pushRight)} onClick={onMarkPaid} disabled={disabled}>
          <span className={classes.markPaidCheckbox}><CheckmarkIcon className={classes.markPaidCheckmark} /></span>{' '}
          {t('enrollmentsBilling.drawer.markAsPaid')}
        </button>
        {!editLocked && (
          <button className={classes.actionButton} onClick={onEdit} disabled={disabled}>
            <EditIcon className={classes.billingActionIcon} /> {t('enrollmentsBilling.drawer.edit')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={classes.actionButtons}>
      {!isFullyRefunded && (
        <button className={classes.actionButton} onClick={onRefund} disabled={disabled}>
          <RefundIcon className={classes.actionIcon} /> {t('enrollmentsBilling.drawer.refund')}
        </button>
      )}
      {/* A refunded/partially-refunded invoice can't be reverted to outstanding, and neither can an
          online payment (Stripe captured the money) or a dropped enrollment. */}
      {isPaid && !editLocked && (
        <button className={classes.actionButton} onClick={onMarkOutstanding} disabled={disabled}>
          {t('enrollmentsBilling.drawer.markAsOutstanding')}
        </button>
      )}
    </div>
  )
}

BillingActionButtons.propTypes = {
  invoice: PropTypes.object,
  editLocked: PropTypes.bool,
  onSendReminder: PropTypes.func,
  onMarkPaid: PropTypes.func,
  onEdit: PropTypes.func,
  onRefund: PropTypes.func,
  onMarkOutstanding: PropTypes.func,
  disabled: PropTypes.bool,
}

// --- Contact Tab ---

const ContactTab = ({ enrollment }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { student, communityEnrollments } = enrollment
  const familyAdmin = student.familyAdmins?.[0]
  const hasGender = familyAdmin?.gender !== null && familyAdmin?.gender !== undefined

  const handleCopy = (e, text) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(text)
    toast.success(t('enrollmentsBilling.drawer.copiedEmail'), { position: toast.POSITION.TOP_RIGHT })
  }

  return (
    <div className={classes.tabContent}>
      <section className={classes.quickActions}>
        <h6 className={classes.sectionTitle}>{t('enrollmentsBilling.drawer.quickActionsAdmin')}</h6>
        <button
          className={classes.quickButton}
          onClick={() => navigate(`/profile/${student.id}`)}
        >
          <PersonIcon width={22} height={22} color={colors.secondaryColor} />
          {t('enrollmentsBilling.drawer.viewProfile')}
        </button>
        {student.familyId && (
          <button
            className={classes.quickButton}
            onClick={() => navigate(`/members/families/${student.familyId}`)}
          >
            <FamilyIcon width={22} height={22} color={colors.secondaryColor} />
            {t('enrollmentsBilling.drawer.viewFamilyPage')}
          </button>
        )}
      </section>

      {familyAdmin && (
        <section className={classes.contactSection}>
          <h6 className={classes.sectionTitle}>{t('enrollmentsBilling.drawer.contactFamilyAdmin')}</h6>
          <div className={classes.adminProfile}>
            <ProfileAvatar student={familyAdmin} avatarClassName='small-round-image-wrapper' />
            <div className={classes.adminInfo}>
              <div className={classes.adminName}>
                <span className='font-weight-bold'>{familyAdmin.firstName} {familyAdmin.lastName}</span>
                <span className={classes.familyAdminLabel}>{t('enrollmentsBilling.drawer.familyAdmin')}</span>
              </div>
              <span className={classes.adminMeta}>
                {familyAdmin.birthdate && t('enrollmentsBilling.drawer.age', { age: familyAdmin.age })}
                {familyAdmin.birthdate && hasGender && ' / '}
                {hasGender && genderLabel[familyAdmin.gender]?.charAt(0)}
              </span>
            </div>
          </div>

          {familyAdmin.phonePrefix && familyAdmin.phone && (
            <a
              className={classes.contactRow}
              href={`tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}`}
            >
              <CallIcon className={classes.contactIcon} />
              <span>{familyAdmin.phonePrefix} {familyAdmin.phone}</span>
            </a>
          )}

          {familyAdmin.email && (
            <a
              className={classes.contactRow}
              href={`mailto:${familyAdmin.email}`}
            >
              <MailIcon className={classes.contactIcon} />
              <div className={classes.emailWrapper}>
                <button
                  onClick={(e) => handleCopy(e, familyAdmin.email)}
                  className={classes.copyButton}
                  aria-label={t('enrollmentsBilling.drawer.copyEmailAddress')}
                >
                  <CopyIcon className={classes.copyIcon} />
                </button>
                {familyAdmin.email}
              </div>
            </a>
          )}
        </section>
      )}

      {communityEnrollments?.length > 0 && (
        <section className={classes.programsSection}>
          <h6 className={classes.sectionTitle}>{t('enrollmentsBilling.drawer.programsCurrentlyEnrolled')}</h6>
          {communityEnrollments.map(e => (
            <div key={e.id} className={classes.programCard}>
              <span className={classes.programName}>{e.courseName}</span>
              {e.courseStartDate && (
                <span className={classes.programMetaRow}>
                  <CalendarIcon className={classes.programCalendarIcon} />
                  <span>
                    {moment(e.courseStartDate).format('MMM D, YYYY')}
                    {e.courseEndDate && ` - ${moment(e.courseEndDate).format('MMM D, YYYY')}`}
                  </span>
                </span>
              )}
              {(e.golfersCount > 0 || e.courseMeetingTimes) && (
                <span className={classes.programMetaRow}>
                  {e.golfersCount > 0 && (
                    <>
                      <GolferIcon className={classes.programGolferIcon} />
                      <span className={classes.programGolferCount}>{e.golfersCount}</span>
                    </>
                  )}
                  {e.courseMeetingTimes && <span>{e.courseMeetingTimes}</span>}
                </span>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

ContactTab.propTypes = {
  enrollment: PropTypes.object.isRequired,
}

// --- Main component PropTypes ---

EnrollmentDetailDrawer.propTypes = {
  enrollment: PropTypes.object.isRequired,
  communityId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onActionComplete: PropTypes.func,
}

export default EnrollmentDetailDrawer
